import { IconType } from 'react-icons';
import { MonitoringCardData, MonitoringMetricValue } from '../types';
import * as Styled from './styles';

export type MetricFormat = 'number' | 'duration' | 'percentage' | 'text';

type MetricCardProps = {
  title: string;
  description?: string;
  data: MonitoringCardData;
  format: MetricFormat;
  icon: IconType;
  accent: string;
};

const numberFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

function formatDuration(value: number) {
  const roundedValue = Math.max(0, Math.round(value));
  const days = Math.floor(roundedValue / 86400);
  const hours = Math.floor((roundedValue % 86400) / 3600);
  const minutes = Math.floor((roundedValue % 3600) / 60);
  const seconds = roundedValue % 60;

  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes > 0) return `${minutes} min ${seconds} s`;
  return `${seconds} s`;
}

function formatValue(value: MonitoringMetricValue, format: MetricFormat) {
  if (value === null || value === '') return 'Sem dados';
  if (typeof value === 'string') return value;
  if (format === 'duration') return formatDuration(value);
  if (format === 'percentage') return `${numberFormatter.format(value)}%`;
  return numberFormatter.format(value);
}

function getExtraFormat(data: MonitoringCardData): MetricFormat {
  if (data.dado_extra?.unidade === 'segundos') return 'duration';
  if (data.dado_extra?.unidade === 'percentual') return 'percentage';
  return 'number';
}

export function MetricCard({ title, description, data, format, icon: Icon, accent }: MetricCardProps) {
  const showUnit = format === 'number' && data.unidade;
  const extraFormat = getExtraFormat(data);

  return (
    <Styled.Card $accent={accent}>
      <Styled.CardHeader>
        <Styled.Title>{title}</Styled.Title>
        <Styled.IconBox $accent={accent} aria-hidden="true">
          <Icon />
        </Styled.IconBox>
      </Styled.CardHeader>
      <Styled.MainValue title={String(data.valor ?? 'Sem dados')}>
        {formatValue(data.valor, format)}
      </Styled.MainValue>
      {showUnit && <Styled.Unit>{data.unidade}</Styled.Unit>}
      {description && <Styled.Description>{description}</Styled.Description>}
      {data.dado_extra && (
        <Styled.ExtraData>
          <span>{data.dado_extra.rotulo}</span>
          <strong>{formatValue(data.dado_extra.valor, extraFormat)}</strong>
        </Styled.ExtraData>
      )}
    </Styled.Card>
  );
}
